import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from 'lucide-react';
import Reveal from './Reveal';
import { contactInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  subject: '',
  message: '',
};

const validateForm = (form) => {
  if (!form.email?.trim()) return 'Email is required.';
  if (!form.message?.trim()) return 'Message is required.';
  return null;
};

const ContactInfoCard = ({ icon: Icon, iconBg, iconColor, label, children }) => (
  <div className="flex items-start gap-4">
    <div
      className={`w-12 h-12 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}
    >
      <Icon size={20} className={iconColor} />
    </div>
    <div>
      <p className="text-[11px] tracking-[0.3em] text-[#6a6a6a] font-semibold mb-1">
        {label}
      </p>
      {children}
    </div>
  </div>
);

const SubmitButton = ({ status }) => {
  let icon = <Send size={16} />;
  let label = 'SEND MESSAGE';
  if (status === 'sending') {
    icon = <Loader2 size={18} className="animate-spin" />;
    label = 'SENDING…';
  } else if (status === 'sent') {
    icon = <CheckCircle2 size={18} />;
    label = 'MESSAGE SENT!';
  }
  return (
    <button
      type="submit"
      disabled={status === 'sending'}
      className="w-full bg-[#e6396b] hover:bg-[#cf2a5b] disabled:opacity-70 text-white font-semibold py-3.5 rounded-lg flex items-center justify-center gap-2 transition-colors text-[13px] tracking-[0.2em]"
    >
      {icon}
      {label}
    </button>
  );
};

const BookingForm = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const submit = useCallback(
    async (payload) => {
      try {
        await axios.post(`${API}/bookings`, payload);
        setStatus('sent');
        setForm(INITIAL_FORM);
        setTimeout(() => setStatus('idle'), 4000);
      } catch (err) {
        setErrorMsg(
          err?.response?.data?.detail ||
            'Something went wrong. Please try again.'
        );
        setStatus('error');
      }
    },
    []
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const error = validateForm(form);
      if (error) {
        setErrorMsg(error);
        setStatus('error');
        return;
      }
      setStatus('sending');
      setErrorMsg('');
      submit(form);
    },
    [form, submit]
  );

  const inputCls =
    'w-full bg-[#fbf6ee] border border-[#e7ddd0] rounded-lg px-4 py-3 text-[15px] text-[#1a1a1a] placeholder-[#9a9a9a] focus:outline-none focus:border-[#e6396b] focus:ring-2 focus:ring-[#e6396b]/20 transition';

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white rounded-2xl shadow-md border border-[#e7ddd0] p-6 md:p-8 space-y-4"
    >
      <div className="grid grid-cols-2 gap-4">
        <input
          name="first_name"
          value={form.first_name}
          onChange={onChange}
          placeholder="First Name"
          className={inputCls}
        />
        <input
          name="last_name"
          value={form.last_name}
          onChange={onChange}
          placeholder="Last Name"
          className={inputCls}
        />
      </div>
      <input
        type="email"
        name="email"
        value={form.email}
        onChange={onChange}
        required
        placeholder="Email *"
        className={inputCls}
      />
      <input
        name="subject"
        value={form.subject}
        onChange={onChange}
        placeholder="Subject (e.g. wedding, theatre, festival)"
        className={inputCls}
      />
      <textarea
        name="message"
        value={form.message}
        onChange={onChange}
        required
        placeholder="Tell us about your event — date, venue, audience size *"
        rows={5}
        className={`${inputCls} resize-none`}
      />

      <SubmitButton status={status} />

      {status === 'error' && errorMsg && (
        <p className="text-sm text-red-600 text-center">{errorMsg}</p>
      )}
      {status === 'sent' && (
        <p className="text-sm text-green-700 text-center">
          Thanks! We'll be in touch within 24 hours.
        </p>
      )}
    </form>
  );
};

const Contact = () => {
  return (
    <section id="contact" className="py-24 md:py-32 bg-[#f5efe7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center mb-14">
          <p className="text-[#e6396b] tracking-[0.4em] text-[12px] font-semibold mb-5">
            GET IN TOUCH
          </p>
          <h2 className="font-serif text-[44px] md:text-[64px] text-[#1a1a1a] font-medium">
            Contact Us
          </h2>
          <p className="mt-4 text-[16px] text-[#6a6a6a] max-w-xl mx-auto">
            Ready to bring the Fleetwood Mac magic to your venue or event? We'd
            love to hear from you!
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 max-w-6xl mx-auto items-start">
          <Reveal className="space-y-7">
            <ContactInfoCard
              icon={Phone}
              iconBg="bg-[#e6396b]/10"
              iconColor="text-[#e6396b]"
              label="PHONE"
            >
              <a
                href={`tel:${contactInfo.phoneRaw}`}
                className="text-[17px] text-[#1a1a1a] hover:text-[#e6396b] transition-colors font-medium"
              >
                {contactInfo.phone}
              </a>
            </ContactInfoCard>

            <ContactInfoCard
              icon={Mail}
              iconBg="bg-[#f6c878]/30"
              iconColor="text-[#b8851f]"
              label="EMAIL"
            >
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-[17px] text-[#1a1a1a] hover:text-[#e6396b] transition-colors font-medium break-all"
              >
                {contactInfo.email}
              </a>
            </ContactInfoCard>

            <ContactInfoCard
              icon={MapPin}
              iconBg="bg-[#e6396b]/10"
              iconColor="text-[#e6396b]"
              label="ADDRESS"
            >
              {contactInfo.addressLines.map((l) => (
                <p
                  key={l}
                  className="text-[16px] text-[#1a1a1a] leading-relaxed"
                >
                  {l}
                </p>
              ))}
            </ContactInfoCard>

            <div className="pt-4 border-t border-[#e7ddd0]">
              <p className="text-[11px] tracking-[0.3em] text-[#6a6a6a] font-semibold mb-3">
                RESPONSE TIME
              </p>
              <p className="text-[15px] text-[#4a4a4a] leading-relaxed">
                We typically reply within{' '}
                <span className="text-[#e6396b] font-semibold">24 hours</span>.
                For urgent enquiries, please call directly.
              </p>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <BookingForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
